import { BigNumber } from 'lib/BigInt'
import Image from 'next/image'
import {
  faBoxBallot,
  faCheck,
  faTimes
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  CollectiveProposal,
  CollectiveProposalQueryResult,
  CollectiveProposalStatus,
  VoteType
} from 'graphql/generated'
import ModalTrigger from '../../common/ModalTrigger'
import { CollectiveCastVoteModal } from '../modals/CastVoteModal'
import Button from 'components/Button'
import { useState } from 'react'
import Typography from 'components/common/Typography'

interface IProps {
  proposal: Pick<
    CollectiveProposal,
    | 'id'
    | 'title'
    | 'status'
    | 'myVote'
    | 'totalVoters'
    | 'isBatch'
    | 'isAuthor'
    | 'canVote'
    | 'isUsingMatchingWallet'
    | 'isPartOfBatch'
    | 'type'
    | 'requireVotingSignature'
  > & {
    proposedBy: {
      user: {
        id: string
      }
    }
  } & {
    collective: Pick<
      NonNullable<
        CollectiveProposalQueryResult['collectiveProposal']
      >['collective'],
      'id'
    >
  }
}

function VoteButton({ proposal }: IProps) {
  const proposalHasVotes = new BigNumber(proposal.totalVoters || 0).gt(0)
  const showFirstVoteCallout = proposal.isAuthor && !proposalHasVotes

  const [voteComplete, setVoteCompleted] = useState(false)

  if (proposal.isBatch) {
    return null
  }

  if (proposal.canVote) {
    return (
      <div className="relative">
        <ModalTrigger
          trigger={onClick => (
            <Button
              icon={faBoxBallot}
              color={'blue'}
              // size={'sm'}
              onClick={onClick}
            >
              {proposal.myVote ? (
                <>
                  <span>Change Vote</span>
                  <span className="inline-block border-r border-white h-[25px] ms-[10px] me-[10px] mb-[-8px]" />
                  <span>{proposal.myVote}</span>
                </>
              ) : (
                'Vote'
              )}
            </Button>
          )}
        >
          <CollectiveCastVoteModal
            proposal={proposal}
            onSuccessDismiss={() => {
              // so it only runs first time you vote
              if (!proposal.myVote) {
                setVoteCompleted(true)
              }
            }}
          />
        </ModalTrigger>

        {showFirstVoteCallout && (
          <div className="absolute bg-white p-[10px] rounded-[10px] border border-gray-100 top-[50px] right-[-10px] w-[200px] z-100  shadow-[0px_4px_45px_rgb(0,0,0,0.04)]">
            <div className="absolute rotate-[10deg] top-[-53px] left-[7px]">
              <Image
                alt="Curly arrow"
                src={'/static/curly-arrow.png'}
                width={471 * 0.13}
                height={458 * 0.13}
                className=""
              />
            </div>
            <p className="text-center pb-[5px] font-normal text-sm text-gray-900">{`Cast the first vote?`}</p>
            <p className="font-light text-sm text-gray-700 text-center">
              {`As the proposal's creator, you may cast the first vote even before voting officially starts.`}
            </p>
          </div>
        )}

        {voteComplete && (
          <div
            className={
              'absolute bg-white p-2 rounded-lg border border-gray-200 shadow-lg top-[calc(100%+60px)] left-[110px] w-[200px] z-2000'
            }
          >
            <div className={'absolute top-[-60px] left-[70px]'}>
              <Image
                alt="Voted share arrow"
                src={'/static/collective/votedShareArrow.png'}
                width={471 * 0.13}
                height={458 * 0.13}
                className=""
              />
            </div>
            <Typography className="text-center">{`Share ${
              proposal.isAuthor ? 'your ' : ''
            } proposal!`}</Typography>
          </div>
        )}
      </div>
    )
  } else if (proposal.myVote) {
    return (
      <div className="bg-gray-100 border border-gray-300 py-[10px] px-[20px] rounded-[10px]">
        <p className="text-sm font-normal text-gray-700">
          <FontAwesomeIcon
            icon={proposal.myVote === VoteType.FOR ? faCheck : faTimes}
          />
          {` \u2004You voted: ${proposal.myVote}`}
        </p>
      </div>
    )
  } else if (proposal.status === CollectiveProposalStatus.PENDING) {
    return (
      <div className="bg-gray-100 border border-gray-300 py-[10px] px-[20px] rounded-[10px]">
        <p className="text-sm font-normal text-gray-700">
          {`Voting will Begin Shortly`}
        </p>
      </div>
    )
  } else if (
    !proposal.myVote &&
    [
      CollectiveProposalStatus.DEFEATED,
      CollectiveProposalStatus.EXECUTED,
      CollectiveProposalStatus.SUCCEEDED,
      CollectiveProposalStatus.EXPIRED
    ].includes(proposal.status)
  ) {
    return (
      <div className="bg-gray-100 border border-gray-300 py-[10px] px-[20px] rounded-[10px]">
        <p className="text-sm font-normal text-gray-700">{`Voting Ended`}</p>
      </div>
    )
  } else if (!proposal.isUsingMatchingWallet) {
    return (
      <div className="bg-gray-100 border border-gray-300 py-[10px] px-[20px] rounded-[10px]">
        <p className="text-sm font-normal text-gray-700">
          <FontAwesomeIcon icon={faBoxBallot} />
          {` Vote`}
        </p>
        <div className="border-t border-t-gray-300 mt-[5px] pt[5px] cursor-not-allowed">
          <p className="text-[11px] font-light w-[130px] text-center text-[#D5193D]">
            {`Wallet mismatch. Are you using the correct wallet?`}
          </p>
        </div>
      </div>
    )
  }

  return null
}

export default VoteButton
